import { api, getAccessTokenForFetch } from '../../api'
import type {
  LivePresenceResponse,
  PresenceClosePayload,
  PresenceConfigResponse,
  PresenceHeartbeatPayload,
  PresenceHeartbeatResponse,
  PresenceRosterParams,
  PresenceRosterResponse,
  PresenceTotalsParams,
  PresenceTotalsResponse,
  PresenceUserDomainUrls,
  PresenceUserDomainUrlsParams,
  PresenceUserDomainsParams,
  PresenceUserDomainsResponse,
  PresenceUserRangeEventsParams,
  PresenceUserRangeEventsResponse,
  PresenceViewerGrantPayload,
  PresenceViewerGrantResponse,
} from '../../../types/presence.types'

export const postHeartbeat = async (payload: PresenceHeartbeatPayload): Promise<PresenceHeartbeatResponse> => {
  const res = await api.post<PresenceHeartbeatResponse>('user/presence/heartbeat/', payload)
  return res.data
}

/**
 * Close a presence session using a keepalive fetch so the request survives page unload.
 * sendBeacon cannot be used here because the app uses Bearer-header auth (no cookies).
 * Errors are swallowed — the backend stale-sweep is the safety net.
 *
 * Token resolution is unload-aware: during pagehide/beforeunload the browser may
 * suspend async work, so an awaited token refresh can be dropped. We first read
 * the cached `id_token` (UserContext refreshes it on every token getter call)
 * SYNCHRONOUSLY, and only fall back to the async getter when the cache is empty.
 * A dropped close is still salvaged by the backend 60s stale-sweep.
 */
export const closePresence = async (payload: PresenceClosePayload): Promise<void> => {
  const baseURL = import.meta.env.VITE_API_ENDPOINT

  let token: string | null = null
  try {
    token = localStorage.getItem('id_token')
  } catch {
    // localStorage unavailable — fall through to the async getter
  }
  if (!token) {
    token = await getAccessTokenForFetch()
  }

  try {
    await fetch(`${baseURL}/user/presence/close/`, {
      method: 'POST',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(payload),
    })
  } catch {
    // Best-effort — backend stale-sweep will clean up any unclosed sessions
  }
}

export const getLivePresence = async (): Promise<LivePresenceResponse> => {
  const res = await api.get<LivePresenceResponse>('user/presence/live/')
  return res.data
}

export const getMyPresence = async (params: PresenceTotalsParams): Promise<PresenceTotalsResponse> => {
  const res = await api.get<PresenceTotalsResponse>('user/presence/me/', { params })
  return res.data
}

export const getUserPresence = async (userId: string, params: PresenceTotalsParams): Promise<PresenceTotalsResponse> => {
  const res = await api.get<PresenceTotalsResponse>(`user/presence/user/${userId}/`, { params })
  return res.data
}

export const getPresenceRoster = async (params: PresenceRosterParams): Promise<PresenceRosterResponse> => {
  const res = await api.get<PresenceRosterResponse>('user/presence/roster/', { params })
  return res.data
}

/**
 * Lazy activity log (actions + notices + clock-ins) for one user across a Range
 * window — fired only on Range-row expand. One bounded, single-user Postgres read.
 */
export const getUserRangeEvents = async (
  userId: string,
  params: PresenceUserRangeEventsParams,
): Promise<PresenceUserRangeEventsResponse> => {
  const res = await api.get<PresenceUserRangeEventsResponse>(
    `user/presence/user/${userId}/events/`,
    { params },
  )
  return res.data
}

/**
 * Lazy per-URL drill-down (7g L4) for one user, one domain, one day — fired only
 * when a domain row in the 'Top sites' block is expanded. One bounded, single-user,
 * single-domain indexed Postgres read (the 30-day hot window).
 */
export const getUserDomainUrls = async (
  userId: string,
  params: PresenceUserDomainUrlsParams,
): Promise<PresenceUserDomainUrls> => {
  const res = await api.get<PresenceUserDomainUrls>(
    `user/presence/user/${userId}/urls/`,
    { params },
  )
  return res.data
}

/**
 * Paginated FULL per-domain list (7f) for one user/day — fired only when the
 * 'Top sites' block's "Other sites" row is expanded (infinite scroll). The roster
 * already ships the top-15 inline; this endpoint returns every domain, paged via
 * NonCountingInfiniteScrollPagination. One bounded, single-user indexed read per page.
 */
export const getUserDomains = async (
  userId: string,
  params: PresenceUserDomainsParams,
): Promise<PresenceUserDomainsResponse> => {
  const res = await api.get<PresenceUserDomainsResponse>(
    `user/presence/user/${userId}/domains/`,
    { params },
  )
  return res.data
}

export const getPresenceConfig = async (): Promise<PresenceConfigResponse> => {
  const res = await api.get<PresenceConfigResponse>('user/config/')
  return res.data
}

export const postPresenceViewerGrant = async (
  payload: PresenceViewerGrantPayload,
): Promise<PresenceViewerGrantResponse> => {
  const res = await api.post<PresenceViewerGrantResponse>('user/presence/grant/', payload)
  return res.data
}
